/** @format */
import config from 'config';
import React, { useCallback, useState } from 'react';
import Image from 'react-bootstrap/Image';
import SimpleImageSlider from 'react-simple-image-slider';
import ObjectListingHeader from '../ObjectListingHeader';
import MapComponent from 'components/GoogleMapComp';
import { useTranslation } from 'react-i18next';
import Accordion from 'react-bootstrap/Accordion';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faEnvelope,
  faUser,
  faFileText
} from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import moment from 'moment';
import 'moment/locale/sv';
import getPropertyTypeTranslation from '../../../../utils/getPropertyTypeTranslation';

import { isInShowBiddingMode } from 'utils/biddingHelper';
import {
  BiddingBanner,
  SoldBanner,
  ComingBanner
} from 'components/PropertyIndicators';
import { getFacts } from './facts';
import { FACTS_FIELD_KEYS } from './utilities';
import factFields from './factFields';
import BlankImage from 'assets/images/images/no-object-image.png';
import { ImageFullScreenPopup } from 'components/FullScreenImage';

import './styles.scss';

export default function ObjectListingContents(props) {
  const { t } = useTranslation();
  const {
    coordinates,
    openBookAViewing,
    openValuationOfMyHome,
    openBidding,
    brokerDetails,
    estate,
    object
  } = props;

  const [selectedFullScreeenImage, setSelectedFullScreenImage] =
    useState(false);

  const handleClickScroll = useCallback((id) => {
    const objectMap = {
      Bilder: 'images',
      Fakta: 'facts',
      Planritning: 'floorPlan',
      Karta: 'map'
    };
    const elements = document.querySelectorAll('#' + objectMap[id]);

    // const header = document.querySelector('.header');

    const mobileStickyHeader = document.querySelector(
      '.imagelist-view-mobile .sticky-box'
    );

    const mobileStickyHeaderHeight =
      typeof mobileStickyHeader.getBoundingClientRect === 'function' &&
      window.innerWidth < 920
        ? mobileStickyHeader.getBoundingClientRect().height
        : 0;

    elements.forEach((element) => {
      if (element) {
        const elementProportions =
          typeof element.getBoundingClientRect === 'function'
            ? element.getBoundingClientRect()
            : {
                height: 0,
                width: 0
              };
        const topPosition = elementProportions.top;

        if (elementProportions.width !== 0 && elementProportions.height !== 0) {
          window.scrollTo({
            top: topPosition + window.scrollY - mobileStickyHeaderHeight,
            behavior: 'smooth'
          });
        }
      }
    });
  }, []);

  const modalButtonsText = t(
    `lang_common:object_listing.listings.modalButtons`
  );
  const objectText = t(`lang_common:object_listing`);

  const viewingArray = estate.viewings ? estate.viewings : [];

  let documentsArray = estate.documents ? estate.documents : [];
  documentsArray = documentsArray?.map((document) => {
    return {
      title: document.name,
      value: document.url
    };
  });

  const getAssessmentsTotal = (assessments) => {
    let total = 0;

    assessments?.forEach((assessment) => {
      assessment?.assessValues?.forEach((assessValue) => {
        total += assessValue.value;
      });
    });

    return total;
  };

  const getBooleanValues = (value) => {
    if (
      value === true ||
      value === 'true' ||
      value === 'yes' ||
      value === 'Yes'
    ) {
      return 'JA';
    } else if (
      value === false ||
      value === 'false' ||
      value === 'no' ||
      value === 'No'
    ) {
      // no and false values should not show
      return null;
    } else {
      return value;
    }
  };

  const getNone = (value) => {
    if (value === 'None') {
      return 'Ej vald';
    }
    return value;
  };

  const getEnergyCompletedStatus = (value) => {
    return objectText?.energyCompleted[value];
  };

  const basicInfoObj = [
    {
      title: objectText?.basicInfoObj?.propertyType.text,
      value: getPropertyTypeTranslation(
        estate?.estateType,
        objectText.propertyType
      )
    },
    {
      title: objectText?.basicInfoObj?.tenure.text,
      value: estate?.baseInformation?.tenure
    },
    {
      title: objectText?.basicInfoObj?.newBuild.text,
      value: getBooleanValues(estate?.baseInformation?.newConstruction)
    },
    {
      title: objectText?.basicInfoObj?.street.text,
      value: estate?.baseInformation?.address?.streetAddress
    },
    {
      title: objectText?.basicInfoObj?.zipCode.text,
      value: estate?.baseInformation?.address?.zipCode
    },
    {
      title: objectText?.basicInfoObj?.municipality.text,
      value: estate?.baseInformation?.address?.municipality
    },
    {
      title: objectText?.basicInfoObj?.city.text,
      value: estate?.baseInformation?.address?.city
    },
    {
      title: objectText?.basicInfoObj?.area.text,
      value: estate?.baseInformation?.address?.area
    },
    {
      title: objectText?.basicInfoObj?.propertyDesignation.text,
      value: estate?.baseInformation?.propertyUnitDesignation
    },
    {
      title: objectText?.basicInfoObj?.keyNumbers.text,
      value: estate?.baseInformation?.keyNumber
    },
    {
      title: objectText?.basicInfoObj?.preInspected.text,
      value: getBooleanValues(estate?.baseInformation?.preliminaryAssessedValue)
    },
    {
      title: objectText?.basicInfoObj?.sellersInsurance.text,
      value: getBooleanValues(estate?.baseInformation?.sellerInsurance)
    },
    {
      title: objectText?.basicInfoObj?.apartmentNumber.text,
      value: estate?.baseInformation?.apartmentNumber
    },
    {
      title: objectText?.basicInfoObj?.apartmentNumberRegistration.text,
      value: estate?.baseInformation?.apartmentNumberRegistration
    },
    {
      title: objectText?.basicInfoObj?.other.text,
      value: estate?.baseInformation?.otherInformation
    },
    {
      title: objectText?.basicInfoObj?.monthlyFee?.label,
      value: estate?.baseInformation?.monthlyFee,
      isCurrency: true
    }
  ];

  const electricityConsumption =
    estate?.electricity?.electricityPowerConsumptionKWH;

  const livingSpace = estate?.baseInformation?.livingSpace;

  const patioSquareMeter =
    estate?.groundIncludetToHousingCooperative?.squereMetre;

  const otherSpace = estate?.baseInformation?.otherSpace;

  const plotArea = estate?.plot?.area;

  const energyConsumption = estate?.energyDeclaration?.energyConsumption;

  const primaryEnergyNumber = estate?.electricity?.primaryEnergyNumber;

  let { factsArry, factsArryExtra, factsArryApartment } = getFacts({
    objectText,
    estate,
    documentsArray,
    electricityConsumption,
    energyConsumption,
    getAssessmentsTotal,
    getBooleanValues,
    getEnergyCompletedStatus,
    getNone,
    livingSpace,
    otherSpace,
    patioSquareMeter,
    plotArea,
    primaryEnergyNumber
  });

  if (object.type === 'Condominium') {
    factsArry = [...factsArry, ...factsArryExtra];
  }

  if (object.type === 'HousingCooperative') {
    factsArry = [...factsArry, ...factsArryApartment];
  }

  const buttonTexts = objectText.listings?.buttons;

  const handleOpenBookAViewing = () => {
    openBookAViewing(true);
  };

  const handleOpenValuationOfMyHome = () => {
    openValuationOfMyHome(true);
  };

  const handleOpenBidding = () => {
    openBidding(true);
  };

  const getAssessmentRow = (label, value, isPrice = false) => {
    const PriceIndicator = () => {
      if (isPrice) {
        return <span className='facts-unit'>Kr</span>;
      }

      return <></>;
    };

    return (
      value !== null &&
      value !== 0 &&
      value !== '' && (
        <div className='object-listing-accordian-list'>
          <div className='object-listing-accordian-list-left'>{label}</div>
          <div className='object-listing-accordian-list-right'>
            {value} <PriceIndicator />
          </div>
        </div>
      )
    );
  };
  const getAssessValueListItem = (label, value, isPrice = false) => {
    const PriceIndicator = () => {
      if (isPrice) {
        return <span className='facts-unit'>Kr</span>;
      }

      return <></>;
    };

    return (
      value !== null &&
      value !== 0 &&
      value !== '' && (
        <li>
          <strong>{label}-</strong> {value} <PriceIndicator />
        </li>
      )
    );
  };

  const getAssessmentTotal = (assessment) => {
    let total = 0;
    assessment?.assessValues?.forEach((assessValue) => {
      total += assessValue.value;
    });
    return total;
  };

  const getAssessmentTable = (assessments) => {
    return assessments?.map((assessment) => (
      <div className='assessment' key={assessment.typeCode}>
        {getAssessmentRow(
          objectText?.assess?.typeCode?.label,
          assessment.typeCode
        )}
        {getAssessmentRow(
          objectText?.assess?.comment?.label,
          assessment.comment
        )}
        {getAssessmentRow(
          objectText?.assess?.taxAssessmentYear?.label,
          assessment.taxYear
        )}{' '}
        {getAssessmentRow(
          objectText?.assess?.assessmentTotal?.label,
          getAssessmentTotal(assessment),
          true
        )}
        {getAssessmentRow(objectText?.assess?.taxFee?.label, assessment.taxFee)}
        {assessment.assessValues.length > 0 && (
          <div className='object-listing-accordian-list'>
            <div className='object-listing-accordian-list-left'>
              {objectText?.assess?.assessValues?.label}
            </div>
            <div className='object-listing-accordian-list-right'></div>
            <div>
              {assessment?.assessValues?.map((assessValue) => {
                return (
                  <ul key={assessValue.value + assessValue.valueYear}>
                    {getAssessValueListItem(
                      objectText?.assess?.evaluationUnit?.label,
                      assessValue.evaluationUnit
                    )}
                    {getAssessValueListItem(
                      objectText?.assess?.value?.label,
                      assessValue.value,
                      true
                    )}
                    {getAssessValueListItem(
                      objectText?.assess?.valueYear?.label,
                      assessValue.valueYear
                    )}
                  </ul>
                );
              })}
            </div>
          </div>
        )}
      </div>
    ));
  };

  const getFactsValues = (facts) => {
    if (facts.title === objectText?.interiorObj?.listOfRooms?.label) {
      return (
        <div className='object-listing-accordian-list object-listing-accordian-list-no-border'>
          <div className='object-listing-accordian-list-left'>
            {facts?.title}
          </div>
          <div className='object-listing-accordian-list-right'></div>
          <div className='list-of-rooms-wrapper'>
            {facts?.value?.map((room) => (
              <div className='list-of-rooms' key={room.heading + room.text}>
                <strong>{room.heading}</strong> - {room.text}
              </div>
            ))}
          </div>
        </div>
      );
    } else {
      if (facts.title === objectText?.assessments.label) {
        if (facts?.value?.length) {
          return (
            <div className='object-listing-accordian-list object-listing-accordian-list-no-border'>
              <div className='object-listing-accordian-list-left'>
                {facts.title}
              </div>
              <div className='object-listing-accordian-list-right'></div>
              <div className='assessments'>
                {getAssessmentTable(facts?.value)}
              </div>
            </div>
          );
        } else {
          return <></>;
        }
      } else if (
        facts.title ===
          objectText?.participationAndRepairFund?.participationOffAnnualFee
            ?.label ||
        facts.title ===
          objectText?.participationAndRepairFund?.participationInAssociation
            ?.label
      ) {
        return (
          facts.value && (
            <div className='object-listing-accordian-list object-listing-accordian-list-no-border'>
              <div className='object-listing-accordian-list-left'>
                {facts.title}
              </div>
              <div className='object-listing-accordian-list-right'>
                {facts.value}%
              </div>
            </div>
          )
        );
      } else if (
        [
          'assessTotal',
          'taxFee',
          'totalAssessedValue',
          'buildingValue',
          'landValue',
          'leasholdFee'
        ].includes(facts?.factFieldName)
      ) {
        return (
          facts.value && (
            <div className='object-listing-accordian-list object-listing-accordian-list-no-border'>
              <div className='object-listing-accordian-list-left'>
                {facts.title}
              </div>
              <div className='object-listing-accordian-list-right'>
                {facts.value} <span className='facts-unit'>Kr</span>
              </div>
            </div>
          )
        );
      } else {
        return (
          facts.value && (
            <div className='object-listing-accordian-list object-listing-accordian-list-no-border'>
              <div className='object-listing-accordian-list-left'>
                {facts.title}
              </div>
              <div className='object-listing-accordian-list-right'>
                {facts.value}
              </div>
            </div>
          )
        );
      }
    }
  };

  const getDocuments = (document) => {
    return (
      <div className='object-listing-accordian-list object-listing-accordian-list-no-border'>
        <div className='object-listing-accordian-list-left'>
          {document.title}
        </div>
        <div className='object-listing-accordian-list-right'>
          <a href={document.value} target='_blank' rel='noreferrer'>
            <Button className={`btn btn-white view-document-button`}>
              <FontAwesomeIcon icon={faFileText} size='lg' />
              {objectText?.document?.download?.label}
            </Button>
          </a>
        </div>
      </div>
    );
  };

  const getBrokerPagePath = () => {
    if (brokerDetails.publicProfileVisibility) {
      // return `/${brokerDetails.brokerSlug}`;
      return `http://profil.friamaklare.se/broker/${brokerDetails.brokerSlug}`;
    }

    return `/hidden`;
  };

  const BiddingInProgressIndicator = (props) => {
    if (isInShowBiddingMode(estate.settings?.bidSetting)) {
      return <BiddingBanner animation={true} {...props} />;
    }
    return <></>;
  };

  const ObjectStatusIndicator = (props) => {
    if (isInShowBiddingMode(estate.settings?.bidSetting)) {
      return (
        <BiddingInProgressIndicator {...props} labelText='Budgivning pågår' />
      );
    } else if (estate?.extra?.advertise?.showAsComming) {
      // Coming
      return (
        <ComingBanner
          {...props}
          labelText={t(
            'lang_common:object_listing:property_status:upcoming:text'
          )}
        />
      );
    } else {
      switch (estate?.extra?.assignment?.status?.id) {
        case '10': {
          // SOLD / reference object
          return (
            <SoldBanner
              {...props}
              labelText={t(
                'lang_common:object_listing:property_status:sold:text'
              )}
            />
          );
        }
        case '4': {
          // SOLD
          return (
            <SoldBanner
              {...props}
              labelText={t(
                'lang_common:object_listing:property_status:sold:text'
              )}
            />
          );
        }
        case '11': {
          // Coming
          return (
            <ComingBanner
              {...props}
              labelText={t(
                'lang_common:object_listing:property_status:upcoming:text'
              )}
            />
          );
        }

        default:
          return <></>;
      }
    }
  };

  const ObjectListing = () => {
    const [currentPrimaryImage, setCurrentPrimaryImage] = React.useState(false);
    let currentImageIndex = 0;

    React.useEffect(() => {
      let interval;
      if (
        estate &&
        Array.isArray(estate?.imageList) &&
        estate.imageList.length > 0
      ) {
        setCurrentPrimaryImage(estate.imageList[currentImageIndex].url);

        interval = setInterval(() => {
          currentImageIndex = (currentImageIndex + 1) % estate.imageList.length;
          setCurrentPrimaryImage(estate.imageList[currentImageIndex].url);
        }, 10000);
      }

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }, []);

    return (
      <div className='object-listing-image-container'>
        <ObjectStatusIndicator />

        <div className='object-listing-primary-image-container'>
          <Image
            rounded
            thumbnail={false}
            src={currentPrimaryImage || BlankImage}
            // src={
            //   estate &&
            //   estate?.imageList.length > 0 &&
            //   estate.imageList?.[0].url
            // }
            className='image-primary-image show'
          />
        </div>
      </div>
    );
  };

  const DataSection = ({
    defaultActiveKey,
    key = 0,
    alwaysOpen,
    headerText,
    additionalHeaderComponents = [],
    children = null,
    labelValueSet = []
  }) => {
    const BodySection = () => {
      if (children) {
        return children;
      }

      return <></>;
    };

    const LabelValueSection = () => {
      if (labelValueSet.length > 0) {
        let response = [];

        labelValueSet.forEach(({ label, value }) => {
          if (value && !Array.isArray(value)) {
            response.push(
              <>
                <div className='object-listing-accordian-list object-listing-accordian-list-no-border'>
                  {label && (
                    <div className='object-listing-accordian-list-left'>
                      {label}
                    </div>
                  )}
                  <div
                    className={`object-listing-accordian-list-${
                      label ? 'right' : 'full'
                    }`}>
                    {value}
                  </div>
                </div>
              </>
            );
          }
        });

        return response;
      }

      return <></>;
    };

    return (
      <Accordion defaultActiveKey={defaultActiveKey} alwaysOpen={alwaysOpen}>
        <Accordion.Item eventKey={0} key={key}>
          <Accordion.Header className='object-listing-accordion-header'>
            <span className='object-listing-accordion-header-title'>
              {headerText}
            </span>
            {additionalHeaderComponents.length > 0 &&
              additionalHeaderComponents.map((element) => element)}
          </Accordion.Header>
          <Accordion.Body>
            <BodySection />
            <LabelValueSection />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  const BrokerInfo = () => (
    <div className='object-listing-contact-card'>
      <div className='object-listing-contact-card-content'>
        <div className='object-listing-contact-card-content-image-container'>
          <a href={getBrokerPagePath()}>
            <img
              src={brokerDetails.profileImageUrl}
              className='object-listing-contact-card-image object-listing-contact-card-image-clickable'
              alt='agent'
            />
          </a>
        </div>
        <div className='object-listing-contact-card-content-container'>
          <div className='object-listing-contact-card-content-details object-listing-contact-card-content-details-left'>
            <div className='object-listing-contact-card-content-name'>
              {brokerDetails.firstName + ' ' + brokerDetails.lastName}
            </div>
            <div className='object-listing-contact-card-content-job'>
              {objectText.contactCard.label.text}
            </div>
            <div className='object-listing-contact-card-content-buttons'>
              <Row>
                <Col xs={12} sm={12} md={6} lg={6}>
                  <a href={`tel:${brokerDetails.phoneNumber}`}>
                    <Button
                      className={` btn-ashe object-listing-contact-card-content-sq-button`}>
                      <FontAwesomeIcon icon={faPhone} size='lg' />
                      {brokerDetails.phoneNumber}
                      {/* 0766503820 */}
                    </Button>
                  </a>
                </Col>
                <Col xs={12} sm={12} md={6} lg={6}>
                  <a href={getBrokerPagePath()}>
                    <Button
                      className={`btn btn-ashe object-listing-contact-card-content-sq-button`}>
                      <FontAwesomeIcon icon={faUser} size='lg' />
                      {modalButtonsText.toBroker?.label}
                    </Button>
                  </a>
                </Col>
                <Col xs={12} sm={12} md={12} lg={12}>
                  <a href={`mailto:${brokerDetails.email}`}>
                    <Button
                      className={`btn btn-ashe object-listing-contact-card-content-sq-button`}>
                      <FontAwesomeIcon icon={faEnvelope} size='lg' />
                      {brokerDetails.email}
                    </Button>
                  </a>
                </Col>
              </Row>
            </div>
          </div>
          <div className='object-listing-contact-card-content-details object-listing-contact-card-content-details-right'>
            <Button
              className={`btn  btn-pink object-listing-contact-card-content-button`}
              onClick={handleOpenBookAViewing}>
              {modalButtonsText.bookAViewing?.label}
            </Button>
            <Button
              className={`btn btn-pink object-listing-contact-card-content-button`}
              onClick={handleOpenValuationOfMyHome}>
              {modalButtonsText.valuationOfMyHome?.label}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const ObjectDescriptionSection = ({
    descriptions = [],
    paragraphClass,
    isFactSection = false,
    descriptionObj = {}
  }) => {
    const descriptionElement = (desc) => {
      const seperatedDesc = desc.split('\n');

      return seperatedDesc.map((paragraph, index) => (
        <p key={index} className={paragraphClass}>
          {paragraph}
        </p>
      ));
    };

    let mainDescription = [];

    if (descriptions[0]) {
      mainDescription = descriptionElement(descriptions[0]);
    } else if (descriptions[1]) {
      mainDescription = descriptionElement(descriptions[1]);
    }

    if (isFactSection) {
      const AdditionalDescription = ({ header, descriptionKey }) => {
        if (descriptionObj[descriptionKey]) {
          return (
            <>
              <h6>{header}</h6>
              {descriptionElement(descriptionObj[descriptionKey])}
            </>
          );
        }
        return <></>;
      };

      return (
        <div className='descriptions-fact-body'>
          {mainDescription}
          <AdditionalDescription
            header={objectText?.descriptionObj?.wayDescription?.text}
            descriptionKey='wayDescription'
          />

          <AdditionalDescription
            header={objectText?.descriptionObj?.other?.text}
            descriptionKey='other'
          />
        </div>
      );
    } else {
      if (mainDescription.length > 0) {
        return mainDescription;
      }

      return <></>;
    }
  };

  const FloorPLanSection = () => {
    if (estate?.imageList && estate.imageList.length > 0) {
      const planImages = estate.imageList
        .filter((item) => item.category === 'Planritningar')
        .map((item) => (
          <Col md={6} key={item?.url}>
            <Image
              rounded
              thumbnail={true}
              src={item?.url}
              className='object-listing-image'
              id={item?.url}
              onClick={() => {
                setSelectedFullScreenImage(item.imageIndex);
              }}
            />
            {item.text && <div className='image-overlay'>{item?.text}</div>}
          </Col>
        ));

      if (planImages.length > 0) {
        return (
          <div className='object-listing-facts' id='floorPlan'>
            <h5 className='object-listing-headling-title'>
              {objectText?.floorPlan.label}
            </h5>
            <Row>{planImages}</Row>
          </div>
        );
      }
    }

    return <></>;
  };

  // eslint-disable-next-line no-unused-vars
  const FactsCount = ({ item, count = null }) => {
    // let factsCount = 0;

    // if (count) {
    //   factsCount = count;
    // } else if (item?.factsCount) {
    //   factsCount = item?.factsCount;
    // }

    // return (
    //   <span key={item?.title} className='facts-count'>
    //     {factsCount} {objectText.facts.label}
    //   </span>
    // );
    return <></>;
  };

  const basicInfoDataSet = (() => {
    let result = [];
    basicInfoObj?.forEach(({ title, value, isCurrency = false }, index) => {
      if (value && !Array.isArray(value)) {
        result.push(
          <React.Fragment key={index}>
            <div className='object-listing-accordian-list object-listing-accordian-list-no-border'>
              <div className='object-listing-accordian-list-left'>{title}</div>
              <div className='object-listing-accordian-list-right'>
                {value} {isCurrency && <span className='facts-unit'>Kr</span>}
              </div>
            </div>
          </React.Fragment>
        );
      }
    });

    return result;
  })();

  const getImages = () => {
    if (estate && estate?.imageList.length > 0) {
      return estate?.imageList?.map((item) => item.url);
    } else {
      return [BlankImage];
    }
  };

  const ObjectImagesSection = () => (
    <div className='object-listing-facts' id='images'>
      <h5 className='object-listing-headling-title'>
        {objectText?.objectImages.label}
      </h5>
      <Row gap={16}>
        {estate.imageList
          .filter((item) => item.category !== 'Planritningar')
          .map((item) => (
            <Col md={6} key={item?.url}>
              <Image
                rounded
                thumbnail={true}
                src={item?.url}
                className='object-listing-image'
                id={item?.url}
                onClick={() => {
                  console.log('---item', item);
                  setSelectedFullScreenImage(item.imageIndex);
                }}
              />
              {item.text && <div className='image-overlay'>{item?.text}</div>}
            </Col>
          ))}
      </Row>
    </div>
  );

  return (
    <>
      <ObjectStatusIndicator isMobile={true} />
      <div className='object-listing-image-slider-mobile'>
        <SimpleImageSlider
          width={350}
          height={250}
          images={getImages()}
          showBullets={false}
          showNavs={true}
          loop={true}
          autoPlay={true}
          autoPlayDelay={10}
        />
      </div>

      <ObjectListing estate={estate} {...props} />
      <ObjectListingHeader
        buttonText={buttonTexts}
        startingPrice={estate?.price?.priceText}
        priceTextLabel={objectText?.starting_price?.label}
        biddingStarted={false}
        // biddingStarted={objectText?.bidding_started?.label}
        finalPrice={objectText?.final_price?.label}
        priceText={estate?.price?.startingPrice || 0}
        finalPriceText={estate?.price?.finalPrice}
        // isBidding={estate?.bids && estate?.bids?.length > 0}
        isBidding={false}
        isFinalPrice={
          estate?.price?.finalPrice && Number(estate?.price?.finalPrice) > 0
        }
        {...props}
        handleClick={handleClickScroll}
        countryCode={object?.countryCode}
        country={object?.country}
        extraData={estate?.extra}
      />

      {/* <h3 className='object-listing-headling-title'>
        {objectText.description.label}
      </h3> */}
      <div className='object-listing-sales-headline'>
        <h5 className='object-listing-headling-title'>
          {estate?.description?.sellingHeading}
        </h5>
        <ObjectDescriptionSection
          descriptions={[estate?.description?.shortSellingDescription]}
          paragraphClass='object-listing-headling-description'
        />

        <Row>
          <Col md={6}>
            <DataSection
              defaultActiveKey={0}
              alwaysOpen={estate.bids.length > 0}
              headerText={objectText?.bidding.label}
              additionalHeaderComponents={
                [
                  // eslint-disable-next-line react/jsx-key
                  // <BiddingInProgressIndicator
                  //   wrapperClassName='object-bidding in-progress-flag'
                  //   bannerClassName='btn-plain'
                  //   labelText='Budgivning pågår'
                  // />
                ]
              }>
              <Button
                className={`btn btn-white object-listing-viewings-button`}
                onClick={() => handleOpenBidding()}>
                {modalButtonsText.viewBiddings?.label}
              </Button>
            </DataSection>
          </Col>
          <Col md={6}>
            <DataSection
              defaultActiveKey={0}
              alwaysOpen={viewingArray.length > 0}
              headerText={objectText?.viewings.label}>
              <div className='object-listing-accordian-viewings'>
                {viewingArray &&
                  viewingArray.length > 0 &&
                  viewingArray?.map((item) => {
                    if (item.showOnInternet) {
                      return (
                        <div
                          className='object-listing-accordian-list'
                          key={item.id}>
                          <div className='object-listing-accordian-list-left object-listing-accordian-list-left-viewings'>
                            {`${moment(item.startTime).format(
                              'dddd D/M'
                            )} kl ${moment(item.startTime).format('HH:mm')}
                                - ${moment(item.endTime).format('HH:mm')}`}
                          </div>
                          <div className='object-listing-accordian-list-left object-listing-accordian-list-left-viewings'>
                            {item.commentary}
                          </div>
                          {/* Removed as a request from client on 2023/02/22 */}
                          {/* <div className='object-listing-accordian-list-left object-listing-accordian-list-left-participants'>
                              {`${item.participants.length} ${
                                item.participants.length !== 1
                                  ? 'participants'
                                  : 'participant'
                              }`}
                            </div> */}
                        </div>
                      );
                    } else {
                      return <></>;
                    }
                  })}

                <Button
                  className={`btn btn-white object-listing-viewings-button`}
                  onClick={() => handleOpenBookAViewing()}>
                  {modalButtonsText.bookAViewing?.label}
                </Button>
              </div>
            </DataSection>
          </Col>
        </Row>
      </div>
      <div className='object-listing-description-headling'>
        <Row>
          <Col md={6}>
            <DataSection
              alwaysOpen={false}
              headerText={objectText?.basicInformation.label}
              // labelValueSet={basicInfoDataSet}
              additionalHeaderComponents={[
                <FactsCount
                  key='base-info-facts-count'
                  count={basicInfoDataSet.length}
                />
              ]}>
              <div className='basic-info'>{basicInfoDataSet}</div>
            </DataSection>
          </Col>
          <Col md={6}>
            <DataSection
              alwaysOpen={false}
              headerText={objectText?.description.label}
              additionalHeaderComponents={[
                <FactsCount
                  key='description-facts-count'
                  count={(() => {
                    let count = 0;

                    if (estate?.description?.longSellingDescription) {
                      count++;
                    }

                    if (estate?.extra?.descriptions?.other) {
                      count++;
                    }

                    if (estate?.extra?.descriptions?.wayDescription) {
                      count++;
                    }

                    return count;
                  })()}
                />
              ]}>
              <>
                <h5 className='object-listing-headling-title'>
                  {estate?.description?.sellingHeading}
                </h5>
                <ObjectDescriptionSection
                  descriptions={[estate?.description?.longSellingDescription]}
                  paragraphClass='object-listing-headling-description'
                  descriptionObj={estate?.extra?.descriptions}
                  isFactSection={true}
                />
              </>
            </DataSection>
          </Col>
        </Row>
      </div>

      <BrokerInfo />

      {/* Facts section */}
      <div className='object-listing-facts' id='facts'>
        <h5 className='object-listing-headling-title'>
          {objectText.facts.label}
        </h5>

        <Row>
          {factsArry?.map((item, index) => {
            if (
              item.title !== objectText?.enrolmentPlans?.label &&
              item.title !== objectText?.waterasw?.label
            ) {
              let contentElementsArray = [];

              if (factFields.factsComponents.includes(item?.factKey)) {
                if (item?.component !== undefined) {
                  const colLength =
                    item?.factKey === FACTS_FIELD_KEYS.AREA ? 12 : 6;

                  return (
                    <Col md={colLength} key={item.title}>
                      <DataSection
                        key={item.title + item}
                        alwaysOpen={false}
                        headerText={item.title}
                        additionalHeaderComponents={[
                          <FactsCount key={index} item={item} />
                        ]}>
                        {item.component}
                      </DataSection>
                    </Col>
                  );
                }
              } else if (item?.factKey === 'documents') {
                item?.body?.forEach(
                  (i) =>
                    i.value &&
                    i.value !== '0' &&
                    i.value !== '' &&
                    contentElementsArray.push(
                      <React.Fragment key={i.title + i.value}>
                        {getDocuments(i)}
                      </React.Fragment>
                    )
                );
              } else if (
                [FACTS_FIELD_KEYS.PARTICIPATION_REPAIR_FUND].includes(
                  item?.factKey
                )
              ) {
                if (item && item?.body.length > 0) {
                  contentElementsArray = item.body;
                }
              } else {
                item &&
                  item?.body.length > 0 &&
                  item?.body?.forEach(
                    (i) =>
                      i.value &&
                      i.value !== '0' &&
                      i.value !== '' &&
                      contentElementsArray.push(
                        <React.Fragment key={i.title + i.value}>
                          {getFactsValues(i)}
                        </React.Fragment>
                      )
                  );
              }

              return (
                <Col md={6} key={item.title}>
                  <DataSection
                    key={item.title + item}
                    alwaysOpen={false}
                    headerText={item.title}
                    additionalHeaderComponents={[
                      <FactsCount
                        key={index}
                        item={item}
                        count={contentElementsArray.length}
                      />
                    ]}>
                    <>
                      {contentElementsArray.map(
                        (contentElement) => contentElement
                      )}
                    </>
                  </DataSection>
                </Col>
              );
            }
          })}
        </Row>
      </div>
      <ObjectImagesSection />
      <FloorPLanSection />
      <div className='object-listing-map' id='map'>
        <h5 className='object-listing-headling-title'>
          {objectText?.map.label}
        </h5>
        <div id='map-wrapper' style={{ height: '400px', width: '100%' }}>
          <MapComponent
            latitude={parseFloat(coordinates.latitud)}
            longitude={parseFloat(coordinates.longitud)}
            googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${config.app.googleMapsApiKey}&v=weekly`}
            loadingElement={<div style={{ height: `100%` }} />}
            containerElement={<div style={{ height: `400px` }} />}
            mapElement={<div style={{ height: `100%` }} />}
            zoomLevel={15}
          />
        </div>
      </div>

      <ImageFullScreenPopup
        display={selectedFullScreeenImage !== false}
        images={estate.imageList || []}
        selectedImage={selectedFullScreeenImage || 0}
        setDisplay={setSelectedFullScreenImage}
      />
    </>
  );
}
